import {
  Fragment,
  Slice,
  type NodeType,
  type Node as PMNode,
  type ResolvedPos,
  type Schema,
} from '@tiptap/pm/model';
import { DRAFT_SECTION_SENTINEL } from '../sections/draft-sentinel';

/** A section title is a level-1 heading; `##`–`#####` are content headings. */
const SECTION_HEADING_LEVEL = 1;

const isSectionHeading = (node: PMNode): boolean =>
  node.type.name === 'heading' && node.attrs.level === SECTION_HEADING_LEVEL;

const isSection = (node: PMNode): boolean => node.type.name === 'sectionNode';

const toChildren = (fragment: Fragment): PMNode[] => {
  const children: PMNode[] = [];
  fragment.forEach((child) => children.push(child));
  return children;
};

/**
 * Containers a pasted section survives in. The document holds one directly; a
 * section body holds one by nesting — `sectionNode` is a `block` whose content
 * is `block+`, so the schema permits one inside another (`isolating` stops
 * content crossing the boundary, it does not stop nesting). A nested section
 * serializes flat to markdown, so it reads as a sibling once the note is saved
 * and reloaded, matching a typed `# ` in the same position.
 */
const SECTION_CAPABLE_CONTAINERS = new Set(['doc', 'sectionNode']);

/**
 * Whether a `sectionNode` can live at `$context`, judged by the first ancestor
 * that holds blocks (skipping the textblock the caret sits in).
 *
 * Deliberately not ProseMirror's `canInsert` walk, which climbs to the document
 * and so answers "true" everywhere. What matters is the container the paste
 * lands in: a list item or table cell cannot keep a section, so fitting would
 * strip the wrapper and strand its title as a bare heading.
 *
 * A code block is a textblock, so the walk skips past it — harmless, since
 * `inCode` bypasses this transform upstream and keeps pasted text literal.
 */
const acceptsSection = ($context: ResolvedPos, sectionType: NodeType): boolean => {
  for (let depth = $context.depth; depth >= 0; depth--) {
    const container = $context.node(depth);
    if (container.isTextblock) continue;
    const index = $context.index(depth);
    return (
      SECTION_CAPABLE_CONTAINERS.has(container.type.name) &&
      container.canReplaceWith(index, index, sectionType)
    );
  }
  return false;
};

/**
 * Group each level-1 heading with the blocks beneath it into a `sectionNode`,
 * stopping at the next heading or an already-built section. Mirrors the
 * absorb-following-blocks rule in `convertParagraphToSection` and the
 * reload-time grouping, so a pasted heading owns the content under it exactly
 * as a typed `# ` does.
 */
const promoteHeadingsToSections = (children: PMNode[], schema: Schema): PMNode[] => {
  const { paragraph, sectionNode } = schema.nodes;
  const promoted: PMNode[] = [];

  let index = 0;
  while (index < children.length) {
    const child = children[index]!;
    index += 1;

    if (!isSectionHeading(child)) {
      promoted.push(child);
      continue;
    }

    const body: PMNode[] = [];
    while (index < children.length) {
      const next = children[index]!;
      if (isSectionHeading(next) || isSection(next)) break;
      body.push(next);
      index += 1;
    }

    promoted.push(
      sectionNode!.createChecked(
        { sectionTitle: child.textContent || null },
        // A section needs a body block; pad an empty paragraph when none follow.
        Fragment.from([child, ...(body.length > 0 ? body : [paragraph!.createChecked()])])
      )
    );
  }

  return promoted;
};

/**
 * Flatten sections and level-1 headings into paragraphs, for targets that
 * cannot hold a section. Without it schema fitting drops the wrapper and the
 * title survives as a bare level-1 heading at full document heading size —
 * exactly what "H1 never renders as an inline heading" exists to prevent.
 */
const demoteSectionsToParagraphs = (children: PMNode[], schema: Schema): PMNode[] => {
  const { paragraph } = schema.nodes;

  const asParagraph = (node: PMNode): PMNode[] => {
    // A draft section's placeholder title is an editor-only sentinel, never
    // user text — flattening it would paste the marker as visible content.
    const isSentinelTitle = node.textContent === DRAFT_SECTION_SENTINEL;
    if (node.content.size === 0 || isSentinelTitle) return [];
    return [paragraph!.createChecked(null, node.content, node.marks)];
  };

  return children.flatMap((child) => {
    if (isSectionHeading(child)) return asParagraph(child);
    if (!isSection(child)) return [child];

    return toChildren(child.content).flatMap((inner) =>
      isSectionHeading(inner) ? asParagraph(inner) : [inner]
    );
  });
};

/**
 * Reconcile a pasted slice's section representation with what the paste target
 * can hold, so every clipboard flavor of the same copy lands the same way.
 *
 * The paths disagree on their own: `clipboardTextParser` parses the plain-text
 * flavor to real `sectionNode`s, while the HTML flavor DOM-parses to a bare
 * level-1 heading. A copy writes both, so the target's flavor negotiation — not
 * the user — decided the structure. Running on the parsed `Slice`, where the
 * paths meet, covers both instead of keeping two implementations in step.
 *
 * `Slice.maxOpen(…, false)` matches the plain-text path's own slice
 * construction, keeping isolating sections closed.
 */
export const normalizePastedSections = (slice: Slice, $context: ResolvedPos): Slice => {
  const { schema } = $context.doc.type;
  const { heading, paragraph, sectionNode } = schema.nodes;
  if (!heading || !paragraph || !sectionNode) return slice;

  const children = toChildren(slice.content);
  const sectionFits = acceptsSection($context, sectionNode);

  const needsChange = sectionFits
    ? children.some(isSectionHeading)
    : children.some((child) => isSection(child) || isSectionHeading(child));
  if (!needsChange) return slice;

  const normalized = sectionFits
    ? promoteHeadingsToSections(children, schema)
    : demoteSectionsToParagraphs(children, schema);

  return Slice.maxOpen(Fragment.fromArray(normalized), false);
};
